import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, makeStyles } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from './Button';
import FormAlert from './FormAlert';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Inter',
    gridGap: theme.spacing(2.25),
    textAlign: 'left',
    '& .MuiFormLabel-root, & .MuiFormHelperText-root': {
      color: theme.palette.type === 'light' ? '#494949' : theme.palette.common.white,
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.25),
  },
  expectationsForm: {
    marginTop: '-55px',
    marginLeft: '-500px',
    // border: '1px solid black',
  },
}));

const propTypes = {
  children: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  isError: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSuccess: PropTypes.bool,
  validationSchema: PropTypes.object.isRequired,
  values: PropTypes.object,
  onReset: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  expectationsForm: PropTypes.bool,
};
const defaultProps = {
  initialValues: undefined,
  isError: false,
  isLoading: false,
  isSuccess: false,
  values: undefined,
  onReset: undefined,
  expectationsForm: false,
};

function Form({
  children,
  initialValues,
  isError,
  isLoading,
  isSuccess,
  validationSchema,
  values,
  onReset,
  onSubmit,
  expectationsForm,
}) {
  const classes = useStyles();

  const methods = useForm({
    defaultValues: initialValues,
    disabled: isLoading,
    mode: 'all',
    resolver: zodResolver(validationSchema),
    values,
  });

  const {
    formState: { isValid, isDirty, isSubmitSuccessful, errors },
    handleSubmit,
    reset,
  } = methods;

  // Handle form submission.
  const handleFormSubmit = formValues => {
    onSubmit(formValues);
  };

  // Reset the form values after successful submission.
  useEffect(() => {
    if (isSuccess && isSubmitSuccessful) {
      reset();
    }
  }, [isSuccess, isSubmitSuccessful, reset]);

  // Notify the parent component to reset the mutation state.
  useEffect(() => {
    if (isDirty) {
      onReset();
    }
  }, [isDirty]);

  return (
    <Box className={classes.container} component="form" onSubmit={handleSubmit(handleFormSubmit)} errors={errors}>
      {children(methods)}

      <Box className={classes.actions} sytle={{ border: '1px solid blue' }}>
        {/* isDirty checks if the form has been modified by the user, if so, the success message will be removed. */}
        {!expectationsForm && <>{isSuccess && !isDirty ? <FormAlert variant="success">Submitted!</FormAlert> : null}</>}

        {isError ? <FormAlert variant="error">Submission failed, please submit again.</FormAlert> : null}

        {!expectationsForm && (
          <Button disabled={!isValid || isLoading} type="submit">
            Submit
          </Button>
        )}
      </Box>
    </Box>
  );
}

Form.propTypes = propTypes;
Form.defaultProps = defaultProps;

export default Form;
